import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Subscription } from 'rxjs';
import { User } from './interfaces';
import { Router, RoutesRecognized } from '@angular/router';
import { filter, pairwise } from 'rxjs/operators';
import { ScopeService } from './scope.service';

import {
  Firestore, //constructor
  collection,
  query,
  where,
  orderBy,
  doc,
  collectionData, //subscription
  getDoc, //promise
  setDoc,
  arrayUnion,
  updateDoc,
  getDocs,
  docData,
  addDoc
} from '@angular/fire/firestore';
import {
  Auth,
  authState,
  signInWithEmailAndPassword,
} from '@angular/fire/auth';

@Injectable({
  providedIn: 'root'
})
export class AuthService implements OnDestroy {
  private canLogin = false;
  private authSub: Subscription;

  public userDetailsObs = new BehaviorSubject<User>({ id: null });
  public gotAuthResponse = new BehaviorSubject<boolean>(false);

  // tslint:disable-next-line:variable-name
  private _userType = 1;
  // tslint:disable-next-line:variable-name
  private _userSub: Subscription;

  // store the URL so we can redirect after logging in
  public redirectUrl: string;

  // tslint:disable-next-line:variable-name
  // private _usersCollection: AngularFirestoreCollection;

  public previousUrl: string;
  private routeSub: Subscription;

  private emailVerified = true;
  private setScopedUser = false;

  private hasRedirected = false;

  constructor(
    private router: Router,
    private afAuth: Auth,
    private afs: Firestore,
    private scopeService: ScopeService
  ) {
    // this.afAuth.setPersistence('session');
    this.authSub = authState(this.afAuth)
      .subscribe(auth => {
        // console.log(auth);
        this.gotAuthResponse.next(true);
        if (auth) {
          this.canLogin = true;
          this.emailVerified = auth.emailVerified;
          this.subscribeToUser(auth.uid, auth.emailVerified);

        } else if (window.location.href.indexOf('signup') > -1 || window.location.href.indexOf('unsubscribe') > -1) {
          // console.log('Did not route away');
        } else { // Not signed in

          this.resetAuth();
        }

      });

    this.router.events
      .pipe(filter((evt: any) => evt instanceof RoutesRecognized), pairwise())
      .subscribe((events: RoutesRecognized[]) => {
        this.previousUrl = events[0].urlAfterRedirects;
        // console.log('current url', events[1].urlAfterRedirects);
      });
  }

  private resetAuth() {
    this.gotAuthResponse.next(true);
    this.canLogin = false;
    this.userDetailsObs.next({ id: null });
    this.redirectUrl = null;
    this.router.navigate(['/auth/signin']);
    this.setScopedUser = false;
  }

  public async login(email, password) {

    try {
      this.gotAuthResponse.next(false);
      await signInWithEmailAndPassword(this.afAuth, email, password);

    } catch (error: any) {
      console.log(error);
      throw new Error(error);
    }
    // return new Promise((resolve, reject) => {
    //   this.afAuth.signInWithEmailAndPassword(email, password)
    //     .then(resp => {
    //       resolve('success');
    //       // The subscriber will catch when ok
    //     })
    //     .catch(error => {
    //       reject(error);
    //     });
    // });
  }

  public logout(): void {
    this.redirectUrl = null;
    if (this._userSub) {
      this._userSub.unsubscribe();
    }
    this.userDetailsObs.next({ id: null });
    this.scopeService.scopedUserId$.next(null);
    this.resetAuth();
    this.afAuth.signOut();
  }

  public checkAuthStatus() {
    return this.canLogin;
  }

  public checkForUser() {
    if (this._userType === 1) {
      return true;
    }
    return false;
  }

  public checkForAdmin() {
    if (this._userType === 0) {
      return true;
    }
    return false;
  }

  public checkEmailVerifiedStatus() {
    return this.emailVerified;
  }



  // private getUserDetails(uid: string): Promise<User> {
  //   this.subscribeToUser(uid);
  //   return new Promise(resolve => {
  //     this._usersCollection.doc(uid).valueChanges()
  //       .pipe(take(1))
  //       .subscribe((user: User) => {
  //         this._userType = user.role;
  //         resolve(user);
  //       });

  //   });
  // }


  // Future<bool> onCheckEmailValidState() async {
  //   // String uid = userId$.value;
  //   // print('Checkin verification state ' + uid);
  //   print(_signedInUid);
  //   if (_signedInUid != null) {
  //     User auth = FirebaseAuth.instance.currentUser;
  //     await auth.reload();
  //     if (auth.emailVerified) {
  //       await FirebaseFirestore.instance
  //           .collection('Users')
  //           .doc(_signedInUid)
  //           .update({'emailVerified': true});
  //       return true;
  //     } else {
  //       // wait a second and try again
  //       Future.delayed(Duration(seconds: 1), () async {
  //         await auth.reload();
  //         if (auth.emailVerified) {
  //           await FirebaseFirestore.instance
  //               .collection('Users')
  //               .doc(_signedInUid)
  //               .update({'emailVerified': true});
  //           return true;
  //         }
  //         return false;
  //       });
  //     }
  //   }
  //   return false;
  // }

  private subscribeToUser(userId: string, emailVerified: boolean) {
    if (this._userSub) {
      this._userSub.unsubscribe();
    }
    // console.log(userId);
    // this._userSub = this.afs.collection('Users').doc<User>(userId).valueChanges()
    this._userSub = docData(doc(this.afs, `Users/${userId}`))
      .subscribe((userDetails: User) => {
        userDetails.id = userId;
        if (userDetails) {
          if (!userDetails.active) {
            this.logout();
            return;
          }

          if (!userDetails.emailVerified && emailVerified) {
            setDoc(doc(this.afs, `Users/${userId}`), { emailVerified: true }, { merge: true });
          }

          if (userDetails.emailVerified) {
            this.emailVerified = true;
          } else {
            this.emailVerified = false;
          }


          this.userDetailsObs.next({ ...userDetails, id: userId });
          this._userType = userDetails.role;
          this.gotAuthResponse.next(true);


          // Scoped user
          // only the first time
          if (!this.setScopedUser) {
            this.setScopedUser = true;
            this.scopeService.setActiveUser({ ...userDetails, id: userId });
            this.scopeService.scopedUserId$.next(userId)
          }

          if (!this.hasRedirected) {
            this.hasRedirected = true;
            if (this.redirectUrl === '/' || !this.redirectUrl) {
              // User type check and then route
              this.router.navigate(['/dashboard']);

            } else {
              this.router.navigateByUrl(this.redirectUrl);
              this.redirectUrl = null;
            }
          }
        }
      }, err => {
        console.log(err);
        // errors when logging out, ignoring
      });
  }


  // public resetPassword(email: string) {
  //   return new Promise((resolve, reject) => {
  //     this.afAuth.sendPasswordResetEmail(email).then(() => {
  //       // Email sent.
  //       resolve({ status: 'success', message: 'Password reset mail sent' });
  //     }, error => {
  //       reject({ status: 'failure', message: error });
  //     });
  //   });
  // }


  ngOnDestroy() {
    if (this._userSub) {
      this._userSub.unsubscribe();
    }
    if (this.authSub) {
      this.authSub.unsubscribe();
    }
    if (this.routeSub) {
      this.routeSub.unsubscribe();
    }
  }




}
